import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Star,
  Trophy,
  CheckCircle,
  X,
  BookOpen
} from 'lucide-react';
import ReviewModal from '@/components/reviews/ReviewModal';
import { ReviewType } from '@/types/review.types';

interface CourseCompletionCelebrationProps {
  courseId: string;
  courseTitle: string;
  courseDuration: string;
  moduleCount: number;
  completionDate: Date;
  totalXP: number;
  accuracy: number;
  isLearner: boolean;
  certificateIssued?: boolean;
  onClose: () => void;
  onDownloadCertificate: () => void;
  onShareAchievement?: () => void;
}

export default function CourseCompletionCelebration({
  courseId,
  courseTitle,
  moduleCount,
  isLearner,
  onClose,
  onDownloadCertificate
}: CourseCompletionCelebrationProps) {
  const navigate = useNavigate();
  const { userMode } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const [showConfetti, setShowConfetti] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    // Show popup after 5 seconds delay
    const showTimer = setTimeout(() => {
      setShowPopup(true);
    }, 5000);

    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => {
    // Stop confetti after 5 seconds of showing the popup
    if (!showPopup) return;

    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [showPopup]);

  const handleGoToCourses = () => {
    // For users with both roles, respect their current mode choice
    // For single-role users, use the isLearner prop from the route
    
    if (userMode.hasBothRoles) {
      // User has both admin and learner roles - respect their current mode
      if (userMode.currentMode === 'admin') {
        navigate('/admin/course/course-catalog');
      } else {
        navigate('/learn');
      }
    } else {
      // User has single role - use the route-based isLearner prop
      if (isLearner) {
        navigate('/learn');
      } else {
        navigate('/admin/course/course-catalog');
      }
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Don't render anything until 5 seconds have passed
  if (!showPopup) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      {/* Confetti Animation */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div
                className={`w-2 h-2 ${
                  i % 4 === 0 ? 'bg-yellow-400' : 
                  i % 4 === 1 ? 'bg-purple-400' : 
                  i % 4 === 2 ? 'bg-green-400' : 
                  'bg-pink-400'
                } rounded-sm animate-spin`}
                style={{ 
                  animationDuration: `${1 + Math.random() * 2}s`,
                  transform: `rotate(${Math.random() * 360}deg)`
                }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Main Modal — capped to the viewport. The outer box clips to the rounded corners and the
          inner box scrolls, so the buttons stay reachable in a short LMS frame. */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-[95%] sm:w-[90%] md:w-[600px] lg:w-[500px] xl:w-[400px] max-w-[90vw] mx-auto h-[30rem] sm:h-[480px] md:h-[500px] lg:h-[500px] xl:h-[520px] max-h-[90vh] animate-bounce-in overflow-hidden">
  {/* Close Button */}
  <button
    onClick={onClose}
    className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 rounded-full transition-colors z-10"
  >
    <X className="w-4 h-4 text-gray-500" />
  </button>

  <div className="h-full overflow-y-auto">
  {/* Header */}
  <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 p-4 text-center">
    <div className="relative">
      <div className="mx-auto w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-xl animate-pulse-scale">
        <Trophy className="w-10 h-10 text-yellow-500" />
      </div>
      <Star className="absolute top-0 left-1/4 w-4 h-4 text-white animate-twinkle" />
      <Star className="absolute top-0 right-1/4 w-3 h-3 text-white animate-twinkle" style={{ animationDelay: '0.5s' }} />
      <Star className="absolute bottom-0 left-1/3 w-4 h-4 text-white animate-twinkle" style={{ animationDelay: '1s' }} />
      <Star className="absolute bottom-0 right-1/3 w-3 h-3 text-white animate-twinkle" style={{ animationDelay: '1.5s' }} />
    </div>

    <h1 className="text-2xl font-bold text-white mt-4 mb-1">Course Completed!</h1>
    <p className="text-sm text-white/90">Congratulations! Your course has been <br/> completed successfully.</p>
  </div>

  {/* Course Details */}
  <div className="p-4">
    <div className="text-center mb-4">
      <h2 className="text-lg font-bold text-gray-900 mb-1">{courseTitle}</h2>
    </div>

    {/* Achievement Stats */}
    <div className="flex justify-center mb-2">
      <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 text-center w-36">
        <div className="flex justify-center mb-1">
          <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
            <CheckCircle className="w-4 h-4 text-white" />
          </div>
        </div>
        <div className="text-lg font-bold text-purple-900">{moduleCount}</div>
        <div className="text-[10px] text-purple-700">Modules Completed</div>
      </div>
    </div>

    {/* Rate this Course Button */}
    {isLearner && (
      <button
        onClick={() => setShowReviewModal(true)}
        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 mb-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-sm font-medium rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all hover:shadow-md"
      >
        <Star className="w-4 h-4" />
        <span>Rate this Course</span>
      </button>
    )}

    {/* Go to Dashboard Button */}
    <button
      onClick={handleGoToCourses}
      className="w-full flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white text-sm font-medium rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all hover:shadow-md"
    >
      <BookOpen className="w-4 h-4" />
      <span>
        {userMode.hasBothRoles
          ? (userMode.currentMode === 'admin'
              ? 'Go to Course Catalog'
              : 'Back to Dashboard')
          : (isLearner ? 'Back to Dashboard' : 'Go to Catalog')}
      </span>
    </button>

  </div>
  </div>

  <style jsx>{`
    @keyframes fall {
      to {
        transform: translateY(calc(100vh + 20px));
      }
    }
    @keyframes bounce-in {
      0% {
        transform: scale(0);
        opacity: 0;
      }
      50% {
        transform: scale(1.1);
      }
      100% {
        transform: scale(1);
        opacity: 1;
      }
    }
    @keyframes pulse-scale {
      0%, 100% {
        transform: scale(1);
      }
      50% {
        transform: scale(1.05);
      }
    }
    @keyframes twinkle {
      0%, 100% {
        opacity: 0.3;
        transform: scale(0.8);
      }
      50% {
        opacity: 1;
        transform: scale(1.2);
      }
    }
    .animate-fall {
      animation: fall linear infinite;
    }
    .animate-bounce-in {
      animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
    }
    .animate-pulse-scale {
      animation: pulse-scale 2s ease-in-out infinite;
    }
    .animate-twinkle {
      animation: twinkle 2s ease-in-out infinite;
    }
  `}</style>
    </div>

      {/* Review Modal */}
      <ReviewModal
        isOpen={showReviewModal}
        onClose={() => setShowReviewModal(false)}
        targetId={courseId}
        targetTitle={courseTitle}
        targetType={ReviewType.COURSE}
        onSuccess={() => setShowReviewModal(false)}
      />
    </div>
  );
}